// /profile/account — personal details, contact, emergency contact, passport.
// An account page (pages/account/kit.js) the app opens as its own native
// screen (ProfileAccountSettings): back closes it ("accountSettingsBack").
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";
import dayjs from "dayjs";
import {
  parseEmergencyNumber,
  validatePhoneNumber,
  checkEmergencyMatchesUser,
  combinePhoneNumber,
} from "../utils/phoneValidation";

import { useGlobalState } from "../GlobalStates";
import { useAuthGuard } from "../services/user";
import { updateUserPersonalInfo } from "../services/user";
import { countries_phone_extension } from "../data/countries";
import { countryToNationality, nationalityToCountry } from "../utils/countryToNationality";
import { Field, Input, Section, Select, Skeleton, Textarea } from "../pages/manage/ui";
import { clubToast } from "../pages/manage/toast";
import { AccountPage, SaveBar } from "../pages/account/kit";

export const AccountSettings = () => {
  const { t } = useTranslation();
  useAuthGuard();

  const [user, setUser] = useGlobalState("user");

  // Form state
  const [formState, setFormState] = useState({
    first_name: "",
    last_name: "",
    display_name: "",
    email: "",
    phone_extension: "",
    phone_number: "",
    emergency_name: "",
    emergency_phone_extension: "",
    emergency_number: "",
    birthday: "",
    gender: "",
    nationality: "",
    non_english_name: "",
    passport: "",
    address: "",
  });

  const [initialFormState, setInitialFormState] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [emergencyNumberError, setEmergencyNumberError] = useState("");
  const [emergencyMatchesUserError, setEmergencyMatchesUserError] = useState("");

  // Generate sorted list of unique nationalities
  const nationalitiesList = useMemo(() => {
    const uniqueNationalities = [...new Set(Object.values(countryToNationality))];
    return uniqueNationalities.sort((a, b) => a.localeCompare(b));
  }, []);
  const phoneCodes = useMemo(() => [...countries_phone_extension].sort((a, b) => a.short_name.localeCompare(b.short_name)), []);

  // Initialize form with user data
  useEffect(() => {
    if (user?.id) {
      // Parse emergency number using utility function
      const { extension, localNumber } = parseEmergencyNumber(user.emergency_number);

      const userData = {
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        display_name: user.display_name || "",
        email: user.email || "",
        phone_extension: user.phone_extension || "+65",
        phone_number: user.phone_number || "",
        emergency_name: user.emergency_name || "",
        emergency_phone_extension: extension,
        emergency_number: localNumber,
        birthday: user.birthday ? dayjs(user.birthday).format("YYYY-MM-DD") : "",
        gender: user.gender || "",
        nationality:
          user.nationality ||
          (user.country ? countryToNationality[user.country.toLowerCase()] || "" : ""),
        non_english_name: user.non_english_name || "",
        passport: user.passport || "",
        address: user.address || "",
      };
      setFormState(userData);
      setInitialFormState(userData);
    }
  }, [user]);

  // Check for changes
  useEffect(() => {
    setHasChanges(!isEqual(formState, initialFormState));
  }, [formState, initialFormState]);

  const handleFormChange = (key, value) => {
    setFormState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  // Validate phone number using utility function
  const validatePhoneNumberWithError = (extension, number) => {
    if (!number) {
      return ""; // Empty is okay, will be caught by required field
    }

    const fullNumber = `${extension}${number}`;
    const isValid = validatePhoneNumber(fullNumber);

    if (isValid) {
      return "";
    } else {
      return t("account_settings.invalid_phone_number", {
        defaultValue: "Invalid phone number",
      });
    }
  };

  // Check if emergency number matches user's phone number with error message
  const checkEmergencyMatchesUserWithError = (emergencyExt, emergencyNum, userExt, userNum) => {
    if (!emergencyNum || !userNum) {
      return "";
    }

    const emergencyFullNumber = `${emergencyExt}${emergencyNum}`;
    const matches = checkEmergencyMatchesUser(emergencyFullNumber, userExt, userNum);

    if (matches) {
      return t("account_settings.emergency_cannot_match_user", {
        defaultValue: "Emergency contact number cannot be the same as your phone number",
      });
    }

    return "";
  };

  // Handle phone number change with validation
  const handlePhoneNumberChange = (value) => {
    const sanitized = value.replace(/\D/g, "");
    handleFormChange("phone_number", sanitized);

    // Validate if not locked
    if (!user.phone_number) {
      const error = validatePhoneNumberWithError(formState.phone_extension, sanitized);
      setPhoneNumberError(error);
    }
  };

  // Handle emergency number change with validation
  const handleEmergencyNumberChange = (value) => {
    const sanitized = value.replace(/\D/g, "");
    handleFormChange("emergency_number", sanitized);

    const error = validatePhoneNumberWithError(formState.emergency_phone_extension, sanitized);
    setEmergencyNumberError(error);

    // Check if emergency number matches user's phone number
    const matchError = checkEmergencyMatchesUserWithError(
      formState.emergency_phone_extension,
      sanitized,
      formState.phone_extension,
      formState.phone_number
    );
    setEmergencyMatchesUserError(matchError);
  };

  // Validate when extension changes
  const handlePhoneExtensionChange = (value) => {
    handleFormChange("phone_extension", value);

    if (!user.phone_number && formState.phone_number) {
      const error = validatePhoneNumberWithError(value, formState.phone_number);
      setPhoneNumberError(error);
    }
  };

  const handleEmergencyExtensionChange = (value) => {
    handleFormChange("emergency_phone_extension", value);

    if (formState.emergency_number) {
      const error = validatePhoneNumberWithError(value, formState.emergency_number);
      setEmergencyNumberError(error);

      // Check if emergency number matches user's phone number
      const matchError = checkEmergencyMatchesUserWithError(
        value,
        formState.emergency_number,
        formState.phone_extension,
        formState.phone_number
      );
      setEmergencyMatchesUserError(matchError);
    }
  };

  const handleSubmitForm = async () => {
    try {
      setSubmitLoading(true);

      // Validate phone numbers before submission
      if (formState.phone_number && !user.phone_number) {
        const phoneError = validatePhoneNumberWithError(
          formState.phone_extension,
          formState.phone_number
        );
        if (phoneError) {
          setPhoneNumberError(phoneError);
          setSubmitLoading(false);
          return;
        }
      }

      if (formState.emergency_number) {
        const emergencyError = validatePhoneNumberWithError(
          formState.emergency_phone_extension,
          formState.emergency_number
        );
        if (emergencyError) {
          setEmergencyNumberError(emergencyError);
          setSubmitLoading(false);
          return;
        }

        // Check if emergency number matches user's phone number
        const matchError = checkEmergencyMatchesUserWithError(
          formState.emergency_phone_extension,
          formState.emergency_number,
          formState.phone_extension,
          formState.phone_number
        );
        if (matchError) {
          setEmergencyMatchesUserError(matchError);
          setSubmitLoading(false);
          return;
        }
      }

      // Combine emergency phone extension and number using utility function
      const {
        emergency_phone_extension,
        emergency_number: emergencyLocalNumber,
        ...restFormState
      } = formState;

      const emergencyNumberCombined = combinePhoneNumber(
        emergency_phone_extension,
        emergencyLocalNumber
      );

      // Derive country from nationality for backend
      const country = formState.nationality
        ? nationalityToCountry[formState.nationality] || ""
        : "";

      const response = await updateUserPersonalInfo({
        ...restFormState,
        emergency_number: emergencyNumberCombined,
        country: country,
      });

      // Update global user state
      setUser({ ...user, ...response.data });

      // Update initial form state to match current state after save
      setInitialFormState({ ...formState });
      setSubmitLoading(false);

      clubToast.success(t("account_settings.save_success", { defaultValue: "Successfully saved!" }));
    } catch (err) {
      setSubmitLoading(false);
      if (err.response?.status === 409) {
        clubToast.error(t("account_settings.phone_already_exists", { defaultValue: "Phone number already exists" }));
      } else if (err.response?.status === 408) {
        clubToast.error(t("account_settings.email_already_exists", { defaultValue: "Email already exists" }));
      } else if (err.response?.status === 410) {
        clubToast.error(
          t("account_settings.emergency_matches_user", {
            defaultValue: "Emergency contact number cannot be the same as your phone number",
          })
        );
        setEmergencyMatchesUserError(
          t("account_settings.emergency_cannot_match_user", {
            defaultValue: "Emergency contact number cannot be the same as your phone number",
          })
        );
      } else {
        clubToast.error(err.message || t("account_settings.error_saving", { defaultValue: "Error saving account information" }));
      }
    }
  };

  const discard = () => {
    setFormState(initialFormState);
    setPhoneNumberError("");
    setEmergencyNumberError("");
    setEmergencyMatchesUserError("");
  };

  const codeOptions = phoneCodes.map((country) => (
    <option key={country.name} value={country.phone_extension}>
      {country.short_name} ({country.phone_extension})
    </option>
  ));

  return (
    <AccountPage
      title={t("account_settings_title", { defaultValue: "Account settings" })}
      back={{ fallback: "/profile", appMessage: "accountSettingsBack" }}
      footer={<SaveBar visible={hasChanges} saving={submitLoading} onSave={handleSubmitForm} onDiscard={discard} />}
      contentClassName={hasChanges ? "tw-pb-32" : ""}
    >
      {!user?.id ? (
        <div className="tw-flex tw-flex-col tw-gap-4">
          <Skeleton className="tw-h-11 tw-w-full" />
          <Skeleton className="tw-h-11 tw-w-full" />
          <Skeleton className="tw-h-11 tw-w-full" />
        </div>
      ) : (
        <>
          <Section first title={t("account_settings_personal", { defaultValue: "Personal information" })}>
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field label={t("account_settings_first_name", { defaultValue: "First name" })}>
                <Input value={formState.first_name} onChange={(e) => handleFormChange("first_name", e.target.value)} autoComplete="given-name" />
              </Field>
              <Field label={t("account_settings_last_name", { defaultValue: "Last name" })}>
                <Input value={formState.last_name} onChange={(e) => handleFormChange("last_name", e.target.value)} autoComplete="family-name" />
              </Field>
              <Field
                className="sm:tw-col-span-2"
                label={t("account_settings.display_name", { defaultValue: "Display name" })}
                hint={t("account_settings.display_name_helper", { defaultValue: "This is how your name appears across the app." })}
              >
                <Input value={formState.display_name} maxLength={60} onChange={(e) => handleFormChange("display_name", e.target.value)} />
              </Field>
              <Field className="sm:tw-col-span-2" label={t("account_settings_native_name", { defaultValue: "Native name" })} optional>
                <Input value={formState.non_english_name} onChange={(e) => handleFormChange("non_english_name", e.target.value)} />
              </Field>
              <Field
                label={t("account_settings.birthday", { defaultValue: "Birthday" })}
                hint={user.birthday ? t("account_settings.birthday_locked", { defaultValue: "Birthday cannot be changed" }) : null}
              >
                <Input type="date" value={formState.birthday} onChange={(e) => handleFormChange("birthday", e.target.value)} disabled={!!user.birthday} />
              </Field>
              <Field label={t("account_settings.gender", { defaultValue: "Gender" })}>
                <Select value={formState.gender} onChange={(e) => handleFormChange("gender", e.target.value)}>
                  <option value="" disabled>
                    {t("account_settings_select", { defaultValue: "Select" })}
                  </option>
                  <option value="Male">{t("account_settings.male", { defaultValue: "Male" })}</option>
                  <option value="Female">{t("account_settings.female", { defaultValue: "Female" })}</option>
                </Select>
              </Field>
              <Field className="sm:tw-col-span-2" label={t("account_settings.nationality", { defaultValue: "Nationality" })}>
                <Select value={formState.nationality} onChange={(e) => handleFormChange("nationality", e.target.value)}>
                  <option value="">{t("account_settings.select_nationality", { defaultValue: "Select nationality" })}</option>
                  {/* A saved value from before the list existed still shows. */}
                  {formState.nationality && !nationalitiesList.includes(formState.nationality) && <option value={formState.nationality}>{formState.nationality}</option>}
                  {nationalitiesList.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </Section>

          <Section title={t("account_settings_contact", { defaultValue: "Contact information" })}>
            <div className="tw-flex tw-flex-col tw-gap-4">
              <Field
                label={t("account_settings.email", { defaultValue: "Email" })}
                hint={user.email ? t("account_settings.email_locked", { defaultValue: "Email cannot be changed" }) : null}
              >
                <Input type="email" value={formState.email} onChange={(e) => handleFormChange("email", e.target.value)} disabled={!!user.email} autoComplete="email" />
              </Field>
              <Field
                label={t("account_settings_phone", { defaultValue: "Phone number" })}
                error={phoneNumberError || null}
                hint={user.phone_number ? t("account_settings.phone_locked", { defaultValue: "Phone number cannot be changed" }) : null}
              >
                <div className="tw-grid tw-grid-cols-[minmax(0,9rem)_1fr] tw-gap-2">
                  <Select value={formState.phone_extension} onChange={(e) => handlePhoneExtensionChange(e.target.value)} disabled={!!user.phone_number} aria-label={t("account_settings.code", { defaultValue: "Code" })}>
                    {codeOptions}
                  </Select>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    pattern="\d*"
                    value={formState.phone_number}
                    onChange={(e) => handlePhoneNumberChange(e.target.value)}
                    disabled={!!user.phone_number}
                    invalid={!!phoneNumberError}
                    autoComplete="tel-national"
                  />
                </div>
              </Field>
            </div>
          </Section>

          <Section title={t("account_settings_emergency", { defaultValue: "Emergency contact" })}>
            <div className="tw-flex tw-flex-col tw-gap-4">
              <Field label={t("account_settings_emergency_name", { defaultValue: "Name" })}>
                <Input value={formState.emergency_name} onChange={(e) => handleFormChange("emergency_name", e.target.value)} />
              </Field>
              <Field label={t("account_settings_phone", { defaultValue: "Phone number" })} error={emergencyNumberError || emergencyMatchesUserError || null}>
                <div className="tw-grid tw-grid-cols-[minmax(0,9rem)_1fr] tw-gap-2">
                  <Select value={formState.emergency_phone_extension} onChange={(e) => handleEmergencyExtensionChange(e.target.value)} aria-label={t("account_settings.code", { defaultValue: "Code" })}>
                    {codeOptions}
                  </Select>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    pattern="\d*"
                    value={formState.emergency_number}
                    onChange={(e) => handleEmergencyNumberChange(e.target.value)}
                    invalid={!!emergencyNumberError || !!emergencyMatchesUserError}
                  />
                </div>
              </Field>
            </div>
          </Section>

          <Section title={t("account_settings_travel", { defaultValue: "Travel details" })}>
            <div className="tw-flex tw-flex-col tw-gap-4">
              <Field label={t("account_settings_passport", { defaultValue: "Passport number" })}>
                <Input value={formState.passport} onChange={(e) => handleFormChange("passport", e.target.value)} autoCapitalize="characters" />
              </Field>
              <Field label={t("account_settings.address", { defaultValue: "Address" })}>
                <Textarea rows={3} value={formState.address} onChange={(e) => handleFormChange("address", e.target.value)} autoComplete="street-address" />
              </Field>
            </div>
          </Section>
        </>
      )}
    </AccountPage>
  );
};

export default AccountSettings;
